import React, { useState, useRef, useEffect, useCallback } from "react";
import "./LicensePlateInput.scss";

const PERSIAN_LETTERS = ["ا", "ب", "پ", "ت", "ث", "ج", "چ", "ح", "خ", "د", "ذ", "ر", "ز", "ژ", "س", "ش", "ص", "ض", "ط", "ظ", "ع", "غ", "ف", "ق", "ک", "گ", "ل", "م", "ن", "و", "ه", "ی"];

const PERSIAN_DIGITS = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
const ARABIC_DIGITS = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];

const MAX_LENGTHS = {
  part1: 2,
  part2: 3,
  part3: 2,
};

const EMPTY_PLATE = { part1: "", letter: "", part2: "", part3: "" };

// Segmented Iranian-plate input rendered as ONE connected plate unit
// (explicit LTR): [IR / flag band] [2 digits | letter | 3 digits] [province code].
// Serialized exactly like the display component expects:
//   "part1 letter part2 | part3"  (e.g. "۱۲ ب ۱۲۳ | ۱۱").
// Contract (unchanged — also used by AddCars):
//   value: that string (or ""), onChange(formattedString), disabled?, hasError?
//
// Letter gate: part2/part3 never accept input or focus while the letter is
// missing — focus is bounced back to the letter field so values can never
// shift or skip the letter section.
function LicensePlateInput({ value, onChange, disabled = false, hasError = false }) {
  const [showLetterPicker, setShowLetterPicker] = useState(false);
  const letterPickerRef = useRef(null);
  const inputsRef = useRef({});
  const pendingFocusRef = useRef(null);
  const lastEmittedRef = useRef("");

  const [plateData, setPlateData] = useState({ ...EMPTY_PLATE });

  const parsePlateValue = useCallback((val) => {
    if (!val) return { ...EMPTY_PLATE };
    if (typeof val === "object") return { ...EMPTY_PLATE, ...val };
    const str = String(val);
    const match = str.match(/^([\d\u06F0-\u06F9\u0660-\u0669\s]+)\s*([^\d\u06F0-\u06F9\u0660-\u0669\s|])\s*([\d\u06F0-\u06F9\u0660-\u0669\s]+)\s*\|\s*([\d\u06F0-\u06F9\u0660-\u0669]+)$/);
    if (match) {
      return {
        part1: match[1].trim(),
        letter: match[2].trim(),
        part2: match[3].trim(),
        part3: match[4].trim(),
      };
    }
    return { ...EMPTY_PLATE };
  }, []);

  const formatPlateValue = useCallback((data) => {
    return `${data.part1} ${data.letter} ${data.part2} | ${data.part3}`;
  }, []);

  const toPersianDigits = useCallback((str) => {
    return String(str ?? "")
      .replace(/[0-9]/g, (d) => PERSIAN_DIGITS[parseInt(d, 10)])
      .replace(/[٠-٩]/g, (d) => PERSIAN_DIGITS[ARABIC_DIGITS.indexOf(d)]);
  }, []);

  const filterNumeric = useCallback((str) => {
    return toPersianDigits(str).replace(/[^۰-۹]/g, "");
  }, [toPersianDigits]);

  const emit = useCallback((next) => {
    const formatted = formatPlateValue(next);
    lastEmittedRef.current = formatted;
    onChange(formatted);
  }, [formatPlateValue, onChange]);

  // Mirror of plateData for event handlers: state is updated together with
  // the ref, so parent onChange is NEVER called inside a state updater
  // (which React reports as setState-during-render).
  const plateDataRef = useRef(plateData);
  const setPlate = useCallback((next) => {
    plateDataRef.current = next;
    setPlateData(next);
    emit(next);
  }, [emit]);

  // Sync only external value changes — ignore our own emitted echoes so
  // mid-typing local state (and caret) is never overwritten/corrupted.
  useEffect(() => {
    if (value === lastEmittedRef.current) return;
    if (!value) {
      lastEmittedRef.current = "";
      const empty = { ...EMPTY_PLATE };
      plateDataRef.current = empty;
      setPlateData(empty);
      return;
    }
    const parts = parsePlateValue(value);
    const hasValidParts = parts.part1 || parts.letter || parts.part2 || parts.part3;
    if (hasValidParts) {
      lastEmittedRef.current = value;
      plateDataRef.current = parts;
      setPlateData(parts);
    }
  }, [value, parsePlateValue]);

  const focusPart = useCallback((part) => {
    const el = inputsRef.current[part];
    if (el) {
      try {
        el.focus();
      } catch {
        // Focus is best-effort (e.g. during unmount).
      }
    } else {
      pendingFocusRef.current = part;
    }
  }, []);

  // --- Numeric sections: each owns ONLY its own part, never overflows -----
  const applyPartChange = useCallback((part, newValue) => {
    const maxLen = MAX_LENGTHS[part];
    const filtered = filterNumeric(newValue).slice(0, maxLen);
    const updatedData = { ...plateDataRef.current, [part]: filtered };
    setPlate(updatedData);
    // Auto-advance only forward along the gated order: part1 → letter
    // (never past the letter), part2 → part3. part3 is the last field.
    if (filtered.length === maxLen && maxLen > 0) {
      if (part === "part1") pendingFocusRef.current = "letter";
      else if (part === "part2") pendingFocusRef.current = "part3";
    }
  }, [filterNumeric, setPlate]);

  const handleNumericInput = useCallback((e, part) => {
    if (disabled) return;
    // Letter gate: numeric sections past the letter stay locked until the
    // required letter exists — bounce focus back instead of accepting input.
    if ((part === "part2" || part === "part3") && !plateDataRef.current.letter) {
      focusPart("letter");
      return;
    }
    applyPartChange(part, e.target.value);
  }, [disabled, applyPartChange, focusPart]);

  // --- Letter section: only a valid plate letter, never skippable ---------
  const handleLetterChange = useCallback((letter) => {
    if (disabled) return;
    const next = letter || "";
    // Reject anything that is not a valid Iranian plate letter.
    if (next && !PERSIAN_LETTERS.includes(next)) return;
    // Advance only AFTER the required letter exists; clearing the letter
    // keeps focus on the letter field so nothing shifts.
    if (next) pendingFocusRef.current = "part2";
    setPlate({ ...plateDataRef.current, letter: next });
    if (next) setShowLetterPicker(false);
  }, [disabled, setPlate]);

  const handleLetterInput = useCallback((e) => {
    if (disabled) return;
    // Take the last typed char so replacing the letter works predictably.
    const chars = String(e.target.value ?? "").replace(/[^\u0600-\u06FF]/g, "");
    handleLetterChange(chars.slice(-1));
  }, [disabled, handleLetterChange]);

  // Focus gate: clicking/tabbing into part2/part3 without the letter bounces
  // back to the letter field — the letter can never be skipped.
  const handleGatedFocus = useCallback((e) => {
    if (disabled) return;
    if (!plateDataRef.current.letter) {
      e.preventDefault();
      focusPart("letter");
    }
  }, [disabled, focusPart]);

  const handleKeyDown = useCallback((e, part) => {
    if (disabled) return;
    if (e.key === "Backspace" && !e.target.value) {
      // Empty-field backspace walks back one section WITHOUT moving values:
      // part2 → letter (never part1), part3 → part2, letter → part1.
      e.preventDefault();
      if (part === "part3") focusPart("part2");
      else if (part === "part2") focusPart("letter");
      else if (part === "letter") focusPart("part1");
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      if (part === "part1") focusPart("letter");
      else if (part === "letter") {
        if (plateDataRef.current.letter) focusPart("part2");
      } else if (part === "part2") {
        if (plateDataRef.current.letter) focusPart("part3");
      } else if (part === "part3") {
        e.target.blur();
      }
    }
  }, [disabled, focusPart]);

  useEffect(() => {
    if (!showLetterPicker) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setShowLetterPicker(false);
        focusPart("letter");
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showLetterPicker, focusPart]);

  useEffect(() => {
    if (pendingFocusRef.current && inputsRef.current[pendingFocusRef.current]) {
      inputsRef.current[pendingFocusRef.current].focus();
      pendingFocusRef.current = null;
    }
  }, [plateData]);

  // --- Paste: full plates distribute across sections, partials stay local --
  const distributeFullPaste = useCallback((text) => {
    const fa = toPersianDigits(text);
    let letterIdx = -1;
    let letterChar = "";
    for (let i = 0; i < fa.length; i += 1) {
      if (PERSIAN_LETTERS.includes(fa[i])) {
        letterIdx = i;
        letterChar = fa[i];
        break;
      }
    }
    if (letterIdx === -1) return null;
    const before = fa.slice(0, letterIdx).replace(/[^۰-۹]/g, "");
    const after = fa.slice(letterIdx + 1).replace(/[^۰-۹]/g, "");
    return {
      part1: before.slice(-MAX_LENGTHS.part1),
      letter: letterChar,
      part2: after.slice(0, MAX_LENGTHS.part2),
      part3: after.slice(MAX_LENGTHS.part2, MAX_LENGTHS.part2 + MAX_LENGTHS.part3),
    };
  }, [toPersianDigits]);

  const focusFirstEmpty = useCallback((data) => {
    if (data.part1.length < MAX_LENGTHS.part1) focusPart("part1");
    else if (!data.letter) focusPart("letter");
    else if (data.part2.length < MAX_LENGTHS.part2) focusPart("part2");
    else if (data.part3.length < MAX_LENGTHS.part3) focusPart("part3");
    else {
      try {
        inputsRef.current.part3?.blur();
      } catch {
        // Blur is best-effort.
      }
    }
  }, [focusPart]);

  const handlePaste = useCallback((e, part) => {
    if (disabled) return;
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text") ?? "";

    // 1) Structured plate ("۱۲ ب ۱۲۳ | ۱۱") → parse directly.
    const structured = parsePlateValue(pastedText);
    const hasStructured = structured.part1 || structured.letter || structured.part2 || structured.part3;
    if (hasStructured && structured.letter) {
      const next = {
        part1: filterNumeric(structured.part1).slice(0, MAX_LENGTHS.part1),
        letter: PERSIAN_LETTERS.includes(structured.letter) ? structured.letter : "",
        part2: filterNumeric(structured.part2).slice(0, MAX_LENGTHS.part2),
        part3: filterNumeric(structured.part3).slice(0, MAX_LENGTHS.part3),
      };
      if (!next.letter) {
        // Pasted plate without a valid letter: keep digits in part1 only and
        // stop at the letter gate.
        setPlate({ ...plateDataRef.current, part1: next.part1 });
        focusPart("letter");
        return;
      }
      setPlate(next);
      focusFirstEmpty(next);
      return;
    }

    // 2) Unstructured full paste ("12ب34567") → split around the letter.
    const distributed = distributeFullPaste(pastedText);
    if (distributed) {
      setPlate(distributed);
      focusFirstEmpty(distributed);
      return;
    }

    // 3) Partial paste → stays in the focused section only (never shifts).
    if (part === "letter") {
      const chars = toPersianDigits(pastedText).replace(/[^\u0600-\u06FF]/g, "");
      handleLetterChange(chars.slice(-1));
      return;
    }
    if ((part === "part2" || part === "part3") && !plateDataRef.current.letter) {
      // Digits pasted past the letter gate go to part1 instead of skipping.
      const digits = filterNumeric(pastedText).slice(0, MAX_LENGTHS.part1);
      const current = plateDataRef.current;
      setPlate({ ...current, part1: digits || current.part1 });
      focusPart("letter");
      return;
    }
    const digits = filterNumeric(pastedText).slice(0, MAX_LENGTHS[part]);
    applyPartChange(part, digits);
  }, [disabled, parsePlateValue, filterNumeric, toPersianDigits, distributeFullPaste, focusFirstEmpty, focusPart, handleLetterChange, applyPartChange, setPlate]);

  return (
    <div className="license-plate-input" dir="ltr">
      <div className={`license-plate-input__plate${hasError ? " license-plate-input__plate--error" : ""}`}>
        <div className="license-plate-input__section license-plate-input__ir" aria-hidden="true">
          <span className="license-plate-input__flag">
            <i className="license-plate-input__flag--green" />
            <i className="license-plate-input__flag--white" />
            <i className="license-plate-input__flag--red" />
          </span>
          <span className="license-plate-input__ir-text">IR</span>
        </div>

        <div className="license-plate-input__section license-plate-input__main">
          <input
            ref={(el) => inputsRef.current.part1 = el}
            className="license-plate-input__field license-plate-input__field--two"
            type="text"
            value={plateData.part1}
            onChange={(e) => handleNumericInput(e, "part1")}
            onKeyDown={(e) => handleKeyDown(e, "part1")}
            onPaste={(e) => handlePaste(e, "part1")}
            maxLength={MAX_LENGTHS.part1}
            disabled={disabled}
            placeholder="۱۲"
            autoComplete="off"
            inputMode="numeric"
            aria-label="دو رقم اول پلاک"
          />
          <span className="license-plate-input__divider" aria-hidden="true" />
          <input
            ref={(el) => inputsRef.current.letter = el}
            className="license-plate-input__field license-plate-input__field--letter"
            type="text"
            value={plateData.letter}
            onChange={handleLetterInput}
            onKeyDown={(e) => handleKeyDown(e, "letter")}
            onPaste={(e) => handlePaste(e, "letter")}
            onClick={() => !disabled && setShowLetterPicker(true)}
            onFocus={() => !disabled && setShowLetterPicker(false)}
            maxLength={1}
            disabled={disabled}
            placeholder="ب"
            autoComplete="off"
            inputMode="text"
            dir="rtl"
            aria-label="حرف پلاک"
          />
          <span className="license-plate-input__divider" aria-hidden="true" />
          <input
            ref={(el) => inputsRef.current.part2 = el}
            className="license-plate-input__field license-plate-input__field--three"
            type="text"
            value={plateData.part2}
            onChange={(e) => handleNumericInput(e, "part2")}
            onKeyDown={(e) => handleKeyDown(e, "part2")}
            onFocus={handleGatedFocus}
            onClick={handleGatedFocus}
            onPaste={(e) => handlePaste(e, "part2")}
            maxLength={MAX_LENGTHS.part2}
            disabled={disabled}
            placeholder="۱۲۳"
            autoComplete="off"
            inputMode="numeric"
            aria-label="سه رقم پلاک"
          />
        </div>

        <div className="license-plate-input__section license-plate-input__end">
          <input
            ref={(el) => inputsRef.current.part3 = el}
            className="license-plate-input__field license-plate-input__field--two"
            type="text"
            value={plateData.part3}
            onChange={(e) => handleNumericInput(e, "part3")}
            onKeyDown={(e) => handleKeyDown(e, "part3")}
            onFocus={handleGatedFocus}
            onClick={handleGatedFocus}
            onPaste={(e) => handlePaste(e, "part3")}
            maxLength={MAX_LENGTHS.part3}
            disabled={disabled}
            placeholder="۱۱"
            autoComplete="off"
            inputMode="numeric"
            aria-label="کد استان"
          />
        </div>
      </div>

      {showLetterPicker && (
        <div
          className="license-plate-input__letter-overlay"
          onClick={() => setShowLetterPicker(false)}
        >
          <div
            className="license-plate-input__letter-card"
            ref={letterPickerRef}
            role="dialog"
            aria-modal="true"
            aria-label="انتخاب حرف پلاک"
            dir="rtl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="license-plate-input__letter-head">
              <p className="license-plate-input__letter-title">حرف پلاک را انتخاب کنید</p>
              <button
                type="button"
                className="license-plate-input__letter-close"
                onClick={() => setShowLetterPicker(false)}
                aria-label="بستن انتخاب حرف"
              >
                ×
              </button>
            </div>
            <div
              className="license-plate-input__letter-grid"
              role="listbox"
              aria-label="حروف پلاک"
            >
              {PERSIAN_LETTERS.map((letter, idx) => (
                <button
                  key={idx}
                  type="button"
                  role="option"
                  className="license-plate-input__letter-option"
                  onClick={() => handleLetterChange(letter)}
                  aria-selected={plateData.letter === letter}
                >
                  {letter}
                </button>
              ))}
            </div>
            <div className="license-plate-input__letter-foot">
              <button
                type="button"
                className="license-plate-input__letter-cancel"
                onClick={() => setShowLetterPicker(false)}
              >
                انصراف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LicensePlateInput;
